import React, { useEffect, useState, Fragment } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
  Link,
} from "@react-pdf/renderer";
import Appendix, { getFontFamilyForText } from "./Appendix";

import NotoSansRegular from "assets/fonts/arial-unicode-ms.ttf";
import NotoSansBold from "assets/fonts/arial-unicode-ms-bold.ttf";
import RobotoBoldItalic from "assets/fonts/Roboto-BoldItalic.ttf";
import RobotoItalic from "assets/fonts/Roboto-Italic.ttf";
import OswaldRegular from "assets/fonts/Oswald-Regular.ttf";
import OswaldBold from "assets/fonts/Oswald-Bold.ttf";
import RobotoRegular from "assets/fonts/Roboto-Regular.ttf";
import RobotoBold from "assets/fonts/Roboto-Bold.ttf";

//components
import PdfTableHeader from "./PdfTableHeader";
import PdfTableBody from "./PdfTableBody";
import ImageCardPdf from "./ImageCardPdf";
import PdfTableBodyNoParty from "./PdfTableBodyNoParty";
import PdfTableHeaderNoParty from "./PdfTableHeaderNoParty";
import PdfTableBodyWeekly from "./PdfTableBodyWeekly";
import PdfTableHeaderWeekly from "./PdfTableHeaderWeekly";
import PdfTableBodyWeeklyNoParty from "./PdfTableBodyWeeklyNoParty";
import PdfTableHeaderWeeklyNoParty from "./PdfTableHeaderWeeklyNoParty";
import PdfTableHeaderTailWind from "./PdfTableHeaderTailWind";
import PdfTableBodyTailWind from "./PdfTableBodyTailWind";
import PdfTableHeaderNoPartyTailWind from "./PdfTableHeaderNoPartyTailWind";
import PdfTableBodyNoPartyTailWind from "./PdfTableBodyNoPartyTailWind";
import PdfTableNoPartyTailWind from "./PdfTableNoPartyTailWind";
import PdfTableHeaderTailWindWeekly from "./PdfTableHeaderTailWindWeekly";
import PdfTableBodyTailWindWeekly from "./PdfTableBodyTailWindWeekly";
import PdfTableHeaderNoPartyTailWindWeekly from "./PdfTableHeaderNoPartyTailWindWeekly";
import PdfTableNoPartyTailWindWeekly from "./PdfTableNoPartyTailWindWeekly";
import TopPosts from "components/Sections/TotalPosts";
import Card from "./Card";
import DateRangeTable from "./PdfDateRangeTable";
import PdfDateRangeTable from "./PdfDateRangeTable";
import { index } from "d3";

const normalizeTwitterPdfData = (data) =>
  data.map((report) =>
    Object.fromEntries(
      Object.entries(report).map(([key, value]) => [
        key,
        Array.isArray(value)
          ? value.map((row) => ({
              ...row,
              total_tweets: row.total_posts,
              total_retweets: row.total_shares,
              num_tweets_in_top: row.num_posts_in_top,
              max_retweets: row.max_shares,
              median_retweets: row.median_shares,
            }))
          : value,
      ])
    )
  );

Font.register({
  family: "Roboto",
  fonts: [
    // { src: RobotoThin, fontWeight: 400 }, // font-style: normal, font-weight: normal
    { src: NotoSansRegular, fontWeight: 400 },
    { src: NotoSansBold, fontWeight: 700 },
  ],
});

Font.register({
  family: "NewRoboto",
  fonts: [
    // { src: RobotoThin, fontWeight: 400 }, // font-style: normal, font-weight: normal
    { src: RobotoRegular, fontWeight: 400 },
    { src: RobotoBold, fontWeight: 700 },
  ],
});

Font.register({
  family: "NewRobotoItalic",
  fonts: [
    // { src: RobotoThin, fontWeight: 400 }, // font-style: normal, font-weight: normal
    { src: RobotoBoldItalic, fontWeight: 700 },
    { src: RobotoItalic, fontWeight: 400 },
  ],
});
Font.register({
  family: "NewRobotoItalicRegular",
  fonts: [
    // { src: RobotoThin, fontWeight: 400 }, // font-style: normal, font-weight: normal
    { src: RobotoItalic, fontWeight: 400 },
  ],
});
Font.register({
  family: "OswaldRegular",
  fonts: [
    // { src: RobotoThin, fontWeight: 400 }, // font-style: normal, font-weight: normal
    { src: OswaldRegular, fontWeight: 400 },
  ],
});
Font.register({
  family: "OswaldBold",
  fonts: [
    // { src: RobotoThin, fontWeight: 400 }, // font-style: normal, font-weight: normal
    { src: OswaldBold, fontWeight: 400 },
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexGrow: 1,
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    paddingTop: 35,
    paddingBottom: 20,
    paddingHorizontal: 23,
  },
  pageWeekly: {
    flexGrow: 1,
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    paddingTop: 35,
    paddingBottom: 20,
    paddingHorizontal: 10,
  },
  body: {
    flexGrow: 1,
  },
  reportHeading: {
    height: 50,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  reportHeadingSmar: {
    height: 90,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  headingText: {
    fontSize: "14.5px",
    textAlign: "center",
    verticalAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    color: "#000000",
    fontFamily: "Roboto",
    fontWeight: 700,
  },
  headingSmarTextTailWind: {
    fontSize: "16px",
    textAlign: "center",
    verticalAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    color: "#000000",
    fontFamily: "NewRoboto",
    fontWeight: 700,
    marginBottom: 2,
    textTransform: "uppercase",
  },
  headingTextTailWind: {
    marginTop: 20,
    fontSize: "14px",
    textAlign: "center",
    verticalAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    color: "#000000",
    fontFamily: "NewRoboto",
    fontWeight: 400,
    textTransform: "uppercase",
  },
  headingTextDate: {
    fontSize: "14px",
    textAlign: "center",
    verticalAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    color: "#000000",
    fontFamily: "Roboto",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  subHeadingText: {
    fontSize: "14px",
    textAlign: "center",
    verticalAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    color: "#000000",
    fontFamily: "NewRoboto",
    fontWeight: 400,
    textTransform: "uppercase",
  },
  headingTextDateTailWind: {
    fontSize: "14px",
    textAlign: "center",
    verticalAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    color: "#000000",
    fontFamily: "Roboto",
    fontWeight: 400,
    // fontStyle: "italic",
    paddingBottom: 4,
  },
  confidentialText: {
    fontSize: "9px",
    textAlign: "right",
    color: "#000000",
    fontFamily: "NewRobotoItalicRegular",
    fontWeight: 400,
    marginBottom: 5,
  },
  reportHeadingNew: {
    height: 50,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 10,
  },
  chartContainer: {},
  chart: {
    marginTop: 30,
    height: "250px",
  },
  headingTextNew: {
    fontSize: "12px",
    textAlign: "center",
    verticalAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    color: "#4F4F4F",
    fontFamily: "NewRoboto",
    fontWeight: 700,
    backgroundColor: "#f2f2f2",
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  headingTextNewTailwind: {
    fontSize: "12px",
    textAlign: "center",
    verticalAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    color: "#000",
    fontFamily: "NewRoboto",
    fontWeight: 700,
    backgroundColor: "#fff",
    paddingHorizontal: 4,
    paddingVertical: 2,
    textTransform: "uppercase",
  },
  subHeadingTextNewTailwind: {
    marginTop: "5px",
    fontSize: "9px",
    textAlign: "center",
    verticalAlign: "center",
    alignItems: "center",
    justifyContent: "flex-start",
    color: "#000",
    fontFamily: "NewRoboto",
    fontWeight: 400,
    backgroundColor: "#fff",
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  subHeadingTextNewTailwindRight: {
    marginTop: "5px",
    fontSize: "9px",
    textAlign: "right",
    verticalAlign: "center",
    alignItems: "center",
    justifyContent: "flex-start",
    color: "#000",
    fontFamily: "NewRoboto",
    fontWeight: 400,
    backgroundColor: "#fff",
    paddingHorizontal: 4,
    paddingVertical: 2,
    textTransform: "capitalize",
  },
  table: {
    marginBottom: 40,
  },
  table_tailwind: {
    marginTop: 35,
  },
  row: {
    flexGrow: 1,
    flexDirection: "row",
    borderBottom: "1px solid #e0e0e0",
    borderLeft: "1px solid #e0e0e0",
    borderRight: "1px solid #e0e0e0",
    justifyContent: "space-around",
    alignContent: "stretch",
    flexWrap: "nowrap",
    alignItems: "stretch",
    flexGrow: 0,
    flexShrink: 0,
    // flexBasis: 42
  },
  imageContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 15,
  },
  container: {
    flexGrow: 1,
    flexDirection: "row",
    // border: "2px solid brown",
    height: 40,
  },
  column_heading: {
    fontSize: "14px",
  },
  section: {
    padding: 10,
    // flexGrow: 1,
    // border: "1px solid red",
  },
  pageNumber: {
    position: "absolute",
    fontSize: 11,
    bottom: 15,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "grey",
  },
  leftAlignedHeading: {
    fontSize: "12px",
    textAlign: "left",
    verticalAlign: "center",
    marginTop: "20px",
    fontFamily: "Roboto",
    fontWeight: "bold",
  },

  tableHeading: {
    fontSize: "10px",
    // textAlign: "left",
    verticalAlign: "center",
    // marginTop: "5px",
    fontWeight: "bold",
    fontFamily: "Roboto",
  },

  inactivePagesText: {
    fontSize: "3px",
  },
});

const options = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
};
const options_range = { year: "numeric", month: "long", day: "numeric" };
const options_range_ireland = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

const strictPageBreak = {
  "Ireland - Wicklow-Wexford": "",
  "Ireland - Wicklow": "Green Party Ireland",
  "Ireland - Wexford": "Independent",
  "Ireland - Waterford": "",
  "Ireland - Tipperary South": "",
  "Ireland - Tipperary North": "Labour",
  "Ireland - Sligo-Leitrim": "Sinn Fein",
  "Ireland - Roscommon-Galway": "Fine Gael",
  "Ireland - Other": "",
  "Ireland - Offaly": "",
  "Ireland - Meath West": "Fine Gael",
  "Ireland - Meath East": "Independent",
  "Ireland - Mayo": "Green Party Ireland",
  "Ireland - Louth": "Independent",
  "Ireland - Longford-Westmeath": "",
  "Ireland - Limerick County": "Independent",
  "Ireland - Limerick City": "Green Party Ireland",
  "Ireland - Laois": "",
  "Ireland - Dublin": "Independent",
  "Ireland - Kildare South": "Fine Gael",
  "Ireland - Kildare North": "Sinn Fein",
  "Ireland - Kerry": "Sinn Fein",
  "Ireland - Galway West": "Independent",
  "Ireland - Galway East": "Independent",
  "Ireland - Dun Laoghaire": "Green Party Ireland",
  "Ireland - Dublin West": "Green Party Ireland",
  "Ireland - Dublin South West": "Green Party Ireland",
  "Ireland - Dublin South Central": "Green Party Ireland",
  "Ireland - Dublin Rathdown": "",
  "Ireland - Dublin North West": "Sinn Fein",
  "Ireland - Dublin Mid West": "Sinn Fein",
  "Ireland - Dublin Fingal West": "",
  "Ireland - Dublin Fingal East": "Fine Gael",
  "Ireland - Dublin Central": "Green Party Ireland",
  "Ireland - Dublin Bay South": "Independent",
  "Ireland - Dublin Bay North": "Fine Gael",
  "Ireland - Donegal": "Independent",
  "Ireland - Cork South West": "Independent",
  "Ireland - Cork South Central": "Fine Gael",
  "Ireland - Cork North West": "",
  "Ireland - Cork North Central": "People Before Profit",
  "Ireland - Cork East": "Labour",
  "Ireland - Clare": "Green Party Ireland",
  "Ireland - Cavan-Monaghan": "",
  "Ireland - Carlow-Kilkenny": "Fine Gael",
  "Ireland National Leaders": "",
  "Ireland TDs": [
    "Aontú",
    "Green Party Ireland",
    "Labour",
    "Right to Change (Independent Group)",
  ],
  "Ireland National Parties": "",
  "Ireland National Ministers": [
    "Fianna Fáil",
    "Fine Gael",
    "Green Party Ireland",
  ],
};

const instaCheck = [
  148, 149, 150, 151, 152, 153, 154, 155, 156, 157, 158, 159, 160, 161, 162,
  163, 164, 165, 166, 167, 168, 169, 170, 171, 172, 173, 174, 175, 176, 177,
  178, 179, 180, 181, 182, 183, 184, 185, 186, 187, 188, 189, 190, 225, 226,
  227, 230,
];

// break={key==="Aontú" ||key==="Green Party Ireland" || key === "Labour" || key === "Right to Change (Independent Group)"}
// break={strictPageBreak?.[`${fileName}`].includes(key)}

// Create Document Component
const GeneratePdf = ({
  completeData,
  startDate,
  endDate,
  fileName,
  platform,
  reportType,
  reportId,
  chartImage,
  analysisData,
  tableData,
}) => {
  const [reportData, setReportData] = useState("");
  const LTP_REPORT_IDS = [18, 19, 20];
  const getLink = (pageId, platform) => {
    if (platform === "facebook") {
      return `https://www.facebook.com/${pageId}`;
    } else if (platform === "instagram") {
      return `https://www.instagram.com/${pageId}`;
    } else {
      return "#";
    }
  };

  function normalizeFancyText(str = "") {
    if (!str) return "";

    // 1️⃣  Normalize combining characters, accents
    let text = str.normalize("NFKC");

    // 2️⃣ Replace Mathematical Bold/Italic/Fraktur/etc letters
    const ranges = [
      [/[\u{1D400}-\u{1D419}]/gu, 0x1d400], // A-Z bold
      [/[\u{1D41A}-\u{1D433}]/gu, 0x1d41a], // a-z bold
      [/[\u{1D434}-\u{1D44D}]/gu, 0x1d434], // A-Z italic
      [/[\u{1D44E}-\u{1D467}]/gu, 0x1d44e], // a-z italic
      [/[\u{1D468}-\u{1D481}]/gu, 0x1d468], // A-Z bold italic
      [/[\u{1D482}-\u{1D49B}]/gu, 0x1d482], // a-z bold italic
      [/[\u{1D49C}-\u{1D4B5}]/gu, 0x1d49c], // script A-Z
      [/[\u{1D4B6}-\u{1D4CF}]/gu, 0x1d4b6], // script a-z
      [/[\u{1D4D0}-\u{1D4E9}]/gu, 0x1d4d0], // bold script A-Z
      [/[\u{1D4EA}-\u{1D503}]/gu, 0x1d4ea], // bold script a-z
      [/[\u{1D504}-\u{1D51D}]/gu, 0x1d504], // Fraktur A-Z
      [/[\u{1D51E}-\u{1D537}]/gu, 0x1d51e], // Fraktur a-z
      [/[\u{1D538}-\u{1D551}]/gu, 0x1d538], // double-struck A-Z
      [/[\u{1D552}-\u{1D56B}]/gu, 0x1d552], // double-struck a-z
      [/[\u{1D56C}-\u{1D585}]/gu, 0x1d56c], // bold Fraktur A-Z
      [/[\u{1D586}-\u{1D59F}]/gu, 0x1d586], // bold Fraktur a-z
      [/[\u{1D5A0}-\u{1D5B9}]/gu, 0x1d5a0], // sans-serif A-Z
      [/[\u{1D5BA}-\u{1D5D3}]/gu, 0x1d5ba], // sans-serif a-z
      [/[\u{1D5D4}-\u{1D5ED}]/gu, 0x1d5d4], // bold sans-serif A-Z
      [/[\u{1D5EE}-\u{1D607}]/gu, 0x1d5ee], // bold sans-serif a-z
      [/[\u{1D608}-\u{1D621}]/gu, 0x1d608], // italic sans-serif A-Z
      [/[\u{1D622}-\u{1D63B}]/gu, 0x1d622], // italic sans-serif a-z
      [/[\u{1D63C}-\u{1D655}]/gu, 0x1d63c], // bold italic sans-serif A-Z
      [/[\u{1D656}-\u{1D66F}]/gu, 0x1d656], // bold italic sans-serif a-z
      [/[\u{1D670}-\u{1D689}]/gu, 0x1d670], // monospace A-Z
      [/[\u{1D68A}-\u{1D6A3}]/gu, 0x1d68a], // monospace a-z
    ];

    for (const [regex, base] of ranges) {
      text = text.replace(regex, (c) =>
        String.fromCharCode(c.codePointAt(0) - base + "A".charCodeAt(0))
      );
    }

    // 3️⃣ Remove invisible characters
    text = text.replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u206F]/g, "");

    return text;
  }

  const getDayName = (dateStr, type, isIreland) => {
    var date = new Date(dateStr);

    // Custom formatting for Ireland
    if (isIreland && type === "range") {
      const day = date.getDate().toString().padStart(2, "0");
      const month = date
        .toLocaleString("default", { month: "short" })
        .slice(0, 3)
        .toUpperCase(); // Sep -> SEP
      const year = date.getFullYear();
      return `${day}-${month}-${year}`; // 07-SEP-2024
    }

    // Default behavior
    const options_range = { year: "numeric", month: "long", day: "numeric" };
    const options_range_ireland = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString(
      undefined,
      type === "range"
        ? isIreland
          ? options_range_ireland
          : options_range
        : options
    );
  };

  const dateDiffInDays = (startDate, endDate) => {
    const date1 = new Date(startDate);
    const date2 = new Date(endDate);
    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    const utc1 = Date.UTC(
      date1.getFullYear(),
      date1.getMonth(),
      date1.getDate()
    );
    const utc2 = Date.UTC(
      date2.getFullYear(),
      date2.getMonth(),
      date2.getDate()
    );
    return Math.floor((utc2 - utc1) / MS_PER_DAY);
  };

  function formatDate(date) {
    const options = { day: "numeric", month: "short", year: "numeric" };
    const formattedDate = new Date(date).toLocaleDateString("en-IN", options);

    // Split the formatted date and construct the desired format
    const [day, month, year] = formattedDate.split(" ");
    return `${day}-${month}-${year}`;
  }

  useEffect(() => {
  
    if (completeData && completeData.length > 0) {
      if (LTP_REPORT_IDS.includes(reportType)) {
        setReportData(completeData[0]);
      } else {
        setReportData(
          platform === "twitter"
            ? normalizeTwitterPdfData(completeData)
            : completeData
        );
      }
    } else {
      setReportData("");
    }
  }, [completeData, platform, reportType]);

  const LTP_REPORT_Styles = StyleSheet.create({
    table: {
      display: "table",
      width: "100%",
      marginBottom: "12px",
      marginTop: "8px",
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: "#ccc",
      // border: "2px solid red",
    },
    row: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#ccc",
      minHeight: 20,
    },
    headerRow: {
      flexDirection: "row",
      backgroundColor: "#eee",
      borderBottomWidth: 2,
      borderBottomColor: "#333",
      minHeight: 26,
    },
    headerCell: {
      borderRightWidth: 1,
      borderRightColor: "#ccc",
      fontSize: 9,
      fontWeight: "bold",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#eee",
      width: "25%",
    },
    indexCell: {
      borderRightWidth: 1,
      borderRightColor: "#ccc",
      fontSize: 8,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#f8f9fa",
      fontWeight: "bold",
      textAlign: "center",
    },
    link: {
      color: "blue",
      textDecoration: "none",
    },
    contentCell: {
      fontSize: 8,
      justifyContent: "center",
      borderRightWidth: 1,
      borderRightColor: "#ccc",
      textAlign: "center",
    },
  });

  const getSummaryTableCategory = (key) => {
    // console.log("Key:", key);
    if (key.toLowerCase().includes("party pages")) {
      return "PARTY";
    }
    if (key.toLowerCase().includes("third party")) {
      return "THIRD PARTY";
    }
    if (key.toLowerCase().includes("leader")) {
      return "LEADER";
    }
    return key;
  };

  const toRoman = (num) => {
    const romans = [
      ["M", 1000],
      ["CM", 900],
      ["D", 500],
      ["CD", 400],
      ["C", 100],
      ["XC", 90],
      ["L", 50],
      ["XL", 40],
      ["X", 10],
      ["IX", 9],
      ["V", 5],
      ["IV", 4],
      ["I", 1],
    ];

    let result = "";
    for (let [letter, value] of romans) {
      while (num >= value) {
        result += letter;
        num -= value;
      }
    }
    return result;
  };
  const priorityOrder = {
    leader: 1,
    party: 2,
    third_party: 3,
  };

  if (
    reportData &&
    !Array.isArray(reportData) &&
    LTP_REPORT_IDS.includes(reportType)
  ) {
    return (
      <>
        <Document>
          <Page size="A4" style={styles.page} orientation="portrait" wrap>
            <View style={styles.body}>
              <View style={styles.reportHeadingSmar}>
                <Text style={styles.headingSmarTextTailWind}>
                  Social Media PERFORMANCE Report
                </Text>
                <Text style={styles.subHeadingText}>
                  ANDHRA PRADESH-IG-TDP PARTY ASSETS
                </Text>
                {(() => {
                  const platformLabels = {
                    instagram: "Instagram",
                    youtube: "Youtube",
                    facebook: "Facebook",
                  };

                  const platformLabel =
                    platformLabels[platform] || "Social Media";

                  const daysDiff = dateDiffInDays(startDate, endDate);

                  // Determine report type based on days difference
                  let reportType;
                  if (daysDiff === 0) {
                    reportType = "Daily Report";
                  } else if (daysDiff === 6) {
                    reportType = "Weekly Report";
                  } else {
                    reportType = "Monthly Report";
                  }

                  // Create date range text
                  const dateText =
                    startDate !== endDate
                      ? `(${getDayName(
                          startDate,
                          "range",
                          true
                        )} - ${getDayName(endDate, "range", true)})`
                      : `(${formatDate(startDate)})`;

                  return (
                    <Text
                      style={[styles.headingTextTailWind, { marginTop: 8 }]}
                    >
                      {`${platformLabel} - ${reportType} `}
                      <Text style={styles.headingTextDateTailWind}>
                        {dateText}
                      </Text>
                    </Text>
                  );
                })()}
              </View>
              <View>
                <Text style={styles.leftAlignedHeading}>
                  I. PAGE PERFORMANCE SUMMARY
                </Text>
                <View style={LTP_REPORT_Styles.table}>
                  {/* TABLE HEADER - Fixed structure */}
                  <View style={LTP_REPORT_Styles.headerRow}>
                    {[
                      "CATEGORY",
                      "ACTIVE PAGES | INACTIVE PAGES",
                      "TOTAL POSTS | MEDIAN POSTS",
                      "MEDIAN LIKES",
                    ].map((cell) => (
                      <View
                        key={`header-${cell}`}
                        style={[
                          LTP_REPORT_Styles.headerCell,
                          {
                            width:
                              cell === "CATEGORY" || cell === "MEDIAN LIKES"
                                ? "17%"
                                : "33%",
                          },
                        ]}
                      >
                        <Text>{cell}</Text>
                      </View>
                    ))}
                  </View>
                  <View>
                    {/* TABLE BODY - Dynamic data */}
                    {Object.entries(reportData).map(
                      ([key, val], index) =>
                        ![
                          "appendix_table",
                          "top_posts",
                          "overall_summary",
                        ].includes(key) && (
                          <View style={LTP_REPORT_Styles.row}>
                            <View
                              style={[
                                LTP_REPORT_Styles.contentCell,
                                { width: "17%" },
                              ]}
                            >
                              <Text>{getSummaryTableCategory(key)}</Text>
                            </View>
                            <View
                              style={[
                                LTP_REPORT_Styles.contentCell,
                                { width: "33%" },
                              ]}
                            >
                              <Text>
                                {val?.summary?.active_pages} |{" "}
                                {val?.summary?.inactive_pages}
                              </Text>
                            </View>
                            <View
                              style={[
                                LTP_REPORT_Styles.contentCell,
                                { width: "33%" },
                              ]}
                            >
                              <Text>
                                {val?.summary?.total_posts} |{" "}
                                {val?.summary?.median_posts}
                              </Text>
                            </View>
                            <View
                              style={[
                                LTP_REPORT_Styles.contentCell,
                                { width: "17%" },
                              ]}
                            >
                              <Text>{val?.summary?.median_likes}</Text>
                            </View>
                          </View>
                        )
                    )}
                    {/* TOTAL ROW */}
                    <View style={LTP_REPORT_Styles.row}>
                      <View
                        style={[
                          LTP_REPORT_Styles.contentCell,
                          { width: "17%" },
                        ]}
                      >
                        <Text>TOTAL</Text>
                      </View>
                      <View
                        style={[
                          LTP_REPORT_Styles.contentCell,
                          { width: "33%" },
                        ]}
                      >
                        <Text>
                          {reportData?.overall_summary?.active_pages} |{" "}
                          {reportData?.overall_summary?.inactive_pages}
                        </Text>
                      </View>
                      <View
                        style={[
                          LTP_REPORT_Styles.contentCell,
                          { width: "33%" },
                        ]}
                      >
                        <Text>
                          {reportData?.overall_summary?.total_posts} |{" "}
                          {reportData?.overall_summary?.median_posts}
                        </Text>
                      </View>
                      <View
                        style={[
                          LTP_REPORT_Styles.contentCell,
                          { width: "17%" },
                        ]}
                      >
                        <Text>
                          {" "}
                          {reportData?.overall_summary?.median_likes}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
              <View>
                {Object.entries(reportData)
                  .filter(
                    ([key]) =>
                      ![
                        "appendix_table",
                        "top_posts",
                        "overall_summary",
                      ].includes(key)
                  )
                  .sort(([keyA], [keyB]) => {
                    const a =
                      priorityOrder[
                        getSummaryTableCategory(keyA).toLowerCase()
                      ] || 999; // items not in list go later
                    const b =
                      priorityOrder[
                        getSummaryTableCategory(keyB).toLowerCase()
                      ] || 999;
                    return a - b;
                  })
                  .map(([key, val], idx) => (
                    <>
                      <View key={key}>
                        <View>
                          <Text style={styles.leftAlignedHeading}>
                            {`${toRoman(idx + 2)}.  ${getSummaryTableCategory(
                              key
                            )} PAGES PERFORMANCE SUMMARY`}
                          </Text>
                          {/* TOP PAGES */}
                          <View>
                            <View wrap={false} style={LTP_REPORT_Styles.table}>
                              <View style={LTP_REPORT_Styles.headerRow}>
                                {
                                  <View
                                    style={[
                                      LTP_REPORT_Styles.headerCell,
                                      { width: "100%" },
                                    ]}
                                  >
                                    <Text style={styles.tableHeading}>
                                      TOP PERFORMING PAGES
                                    </Text>
                                  </View>
                                }
                              </View>
                              <View style={LTP_REPORT_Styles.headerRow}>
                                {[
                                  "SNO",
                                  "NAME",
                                  "TOTAL POSTS",
                                  "TOTAL LIKES",
                                  "TOTAL ENGAGEMENTS",
                                  "MAX LIKES",
                                ].map((h) => (
                                  <View
                                    key={h}
                                    style={[
                                      LTP_REPORT_Styles.headerCell,
                                      {
                                        width:
                                          h === "NAME" ||
                                          h === "TOTAL ENGAGEMENTS"
                                            ? "30%"
                                            : h === "SNO"
                                            ? "10%"
                                            : "20%",
                                      },
                                    ]}
                                  >
                                    <Text>{h}</Text>
                                  </View>
                                ))}
                              </View>

                              {val.top_5_pages?.map((row, i) => (
                                <View key={i} style={LTP_REPORT_Styles.row}>
                                  <View
                                    style={[
                                      LTP_REPORT_Styles.contentCell,
                                      { width: "10%" },
                                    ]}
                                  >
                                    <Text>{i + 1}</Text>
                                  </View>
                                  <View
                                    style={[
                                      LTP_REPORT_Styles.contentCell,
                                      { width: "30%" },
                                    ]}
                                  >
                                    <Link
                                      src={getLink(row.page_id, platform)}
                                      style={LTP_REPORT_Styles.link}
                                    >
                                      <Text
                                        style={{
                                          fontFamily: getFontFamilyForText(
                                            normalizeFancyText(row.page_name)
                                          ),
                                        }}
                                      >
                                        {normalizeFancyText(row.page_name)}
                                      </Text>
                                    </Link>
                                  </View>
                                  <View
                                    style={[
                                      LTP_REPORT_Styles.contentCell,
                                      { width: "20%" },
                                    ]}
                                  >
                                    <Text>{row.total_posts}</Text>
                                  </View>
                                  <View
                                    style={[
                                      LTP_REPORT_Styles.contentCell,
                                      { width: "20%" },
                                    ]}
                                  >
                                    <Text>{row.total_likes}</Text>
                                  </View>
                                  <View
                                    style={[
                                      LTP_REPORT_Styles.contentCell,
                                      { width: "30%" },
                                    ]}
                                  >
                                    <Text>{row.total_engagement}</Text>
                                  </View>
                                  <View
                                    style={[
                                      LTP_REPORT_Styles.contentCell,
                                      { width: "20%" },
                                    ]}
                                  >
                                    <Text>{row.max_likes}</Text>
                                  </View>
                                </View>
                              ))}
                            </View>
                          </View>
                        </View>
                        {/* LEAST PERFORMING PAGES */}
                        <View>
                          <View wrap={false} style={LTP_REPORT_Styles.table}>
                            <View style={LTP_REPORT_Styles.headerRow}>
                              {
                                <View
                                  style={[
                                    LTP_REPORT_Styles.headerCell,
                                    { width: "100%" },
                                  ]}
                                >
                                  <Text style={styles.tableHeading}>
                                    BOTTOM PERFORMING PAGES
                                  </Text>
                                </View>
                              }
                            </View>
                            <View style={LTP_REPORT_Styles.headerRow}>
                              {[
                                "SNO",
                                "NAME",
                                "TOTAL POSTS",
                                "TOTAL LIKES",
                                "TOTAL ENGAGEMENTS",
                                "MAX LIKES",
                              ].map((h) => (
                                <View
                                  key={h}
                                  style={[
                                    LTP_REPORT_Styles.headerCell,
                                    {
                                      width:
                                        h === "NAME" ||
                                        h === "TOTAL ENGAGEMENTS"
                                          ? "30%"
                                          : h === "SNO"
                                          ? "10%"
                                          : "20%",
                                    },
                                  ]}
                                >
                                  <Text>{h}</Text>
                                </View>
                              ))}
                            </View>

                            {val.bottom_5_pages?.map((row, i) => (
                              <View key={i} style={LTP_REPORT_Styles.row}>
                                <View
                                  style={[
                                    LTP_REPORT_Styles.contentCell,
                                    { width: "10%" },
                                  ]}
                                >
                                  <Text>{i + 1}</Text>
                                </View>
                                <View
                                  style={[
                                    LTP_REPORT_Styles.contentCell,
                                    { width: "30%" },
                                  ]}
                                >
                                  <Link
                                    src={getLink(row.page_id, platform)}
                                    style={LTP_REPORT_Styles.link}
                                  >
                                    <Text>
                                      {normalizeFancyText(row.page_name)}
                                    </Text>
                                  </Link>
                                </View>
                                <View
                                  style={[
                                    LTP_REPORT_Styles.contentCell,
                                    { width: "20%" },
                                  ]}
                                >
                                  <Text>{row.total_posts}</Text>
                                </View>
                                <View
                                  style={[
                                    LTP_REPORT_Styles.contentCell,
                                    { width: "20%" },
                                  ]}
                                >
                                  <Text>{row.total_likes}</Text>
                                </View>
                                <View
                                  style={[
                                    LTP_REPORT_Styles.contentCell,
                                    { width: "30%" },
                                  ]}
                                >
                                  <Text>{row.total_engagement}</Text>
                                </View>
                                <View
                                  style={[
                                    LTP_REPORT_Styles.contentCell,
                                    { width: "20%" },
                                  ]}
                                >
                                  <Text>{row.max_likes}</Text>
                                </View>
                              </View>
                            ))}
                          </View>
                        </View>
                        {/* INACTIVE MINISTERS LIST */}
                        <View>
                          {key.toLowerCase().includes("third party") ? (
                            <View wrap={false} style={LTP_REPORT_Styles.table}>
                              <View style={LTP_REPORT_Styles.row}>
                                <View
                                  style={[
                                    LTP_REPORT_Styles.contentCell,
                                    { width: "70%" },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      LTP_REPORT_Styles.tableHeading,
                                      {
                                        fontWeight: "bold",
                                        fontFamily: "Roboto",
                                      },
                                    ]}
                                  >
                                    COUNT OF INACTIVE THIRD PARTY PAGES
                                  </Text>
                                </View>
                                <View
                                  style={[
                                    LTP_REPORT_Styles.contentCell,
                                    { width: "30%" },
                                  ]}
                                >
                                  <Text>{val.inactive_pages?.length}</Text>
                                </View>
                              </View>
                            </View>
                          ) : (
                            <View style={LTP_REPORT_Styles.table}>
                              <View style={LTP_REPORT_Styles.headerRow} fixed>
                                <View
                                  style={[
                                    LTP_REPORT_Styles.headerCell,
                                    { width: "100%" },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      LTP_REPORT_Styles.tableHeading,
                                      {
                                        fontWeight: "bold",
                                        fontFamily: "Roboto",
                                      },
                                    ]}
                                  >
                                    INACTIVE PAGES
                                  </Text>
                                </View>
                              </View>
                              {/* show inactive pages as rows in table (4 columns) */}
                              {(() => {
                                const pages = val.inactive_pages || [];

                                // Break array into chunks of 4
                                const rows = [];
                                for (let i = 0; i < pages.length; i += 4) {
                                  rows.push(pages.slice(i, i + 4));
                                }

                                return rows.map((row, rowIndex) => (
                                  <View
                                    wrap={false}
                                    key={rowIndex}
                                    style={LTP_REPORT_Styles.row}
                                  >
                                    {row.map((page, colIndex) => (
                                      <View
                                        key={colIndex}
                                        style={[
                                          LTP_REPORT_Styles.contentCell,
                                          { width: "25%" },
                                        ]} // 4 columns
                                      >
                                        <Link
                                          src={getLink(page.page_id, platform)}
                                          style={LTP_REPORT_Styles.link}
                                        >
                                          <Text
                                            style={{
                                              fontFamily: getFontFamilyForText(
                                                normalizeFancyText(
                                                  page.page_name
                                                )
                                              ),
                                            }}
                                          >
                                            {normalizeFancyText(page.page_name)}
                                          </Text>
                                        </Link>
                                      </View>
                                    ))}

                                    {/* Fill empty cells if row has less than 4 items */}
                                    {Array.from({ length: 4 - row.length }).map(
                                      (_, fillIndex) => (
                                        <View
                                          key={`empty-${fillIndex}`}
                                          style={[
                                            LTP_REPORT_Styles.contentCell,
                                            { width: "25%" },
                                          ]}
                                        />
                                      )
                                    )}
                                  </View>
                                ));
                              })()}
                            </View>
                          )}
                        </View>
                      </View>
                      <View break />
                    </>
                  ))}
              </View>
              <View break />
              <View>
                <View>
                  <Text style={styles.leftAlignedHeading}>V. TOP 09 POSTS</Text>
                  <View style={styles.imageContainer}>
                    {(reportData?.top_posts ?? []).slice(0, 9).map((imgVal) => {
                      return (
                        <Card
                          key={imgVal.post_id}
                          title={imgVal.page_name}
                          description={imgVal.message}
                          shares={imgVal.shares}
                          likes={imgVal.likes}
                          comments={imgVal.comments}
                          imageUrl={imgVal.resource}
                          postLink={imgVal.post_link}
                          platform={platform}
                          reportType={reportType}
                        />
                      );
                    })}
                  </View>
                </View>
              </View>
              <View break={true} />
              {Object.entries(reportData).map(([key, val], idx) =>
                getSummaryTableCategory(key).toLowerCase() === "third party" ? (
                  <View key={key}>
                    <Appendix
                      startIndex={1}
                      columns={4}
                      index={false}
                      headerRows={[
                        {
                          cells: [{ text: "Appendix", colSpan: 4 }],
                        },
                        {
                          cells: [
                            {
                              text: `${getSummaryTableCategory(
                                key
                              )} Inactive Pages`,
                              colSpan: 4,
                            },
                          ],
                        },
                      ]}
                      pages={(reportData[key]?.inactive_pages || []).map(
                        (page) => ({
                          title: normalizeFancyText(page.page_name),
                          link: getLink(page.page_id, platform),
                        })
                      )}
                    />
                  </View>
                ) : null
              )}
            </View>
          </Page>
        </Document>
      </>
    );
  }

  return (
    <>
      {reportData &&
      reportData.length > 0 &&
      reportType !== 9 &&
      reportType !== 10 && reportType!==11 ? (
        <Document>
          <Page size="A4" style={styles.page} orientation="portrait" wrap>
            <View style={styles.body}>
              <View style={styles.reportHeading}>
                <Text style={styles.headingText}>
                  {fileName}{" "}
                  {dateDiffInDays(startDate, endDate) === 0
                    ? "- Daily Report"
                    : dateDiffInDays(startDate, endDate) === 6
                    ? "- Weekly Report"
                    : "- Monthly Report"}
                </Text>
                <Text style={styles.headingTextDate}>
                  {startDate !== endDate
                    ? `(${getDayName(startDate, "range")} - ${getDayName(
                        endDate,
                        "range"
                      )})`
                    : `(${getDayName(startDate, "daily")})`}
                </Text>
              </View>
              {reportData.length > 0 ? (
                reportData.map((val, indx) => {
                  return (
                    <Fragment>
                      {val?.top_pages ? (
                        startDate === endDate ? (
                          <View style={styles.table}>
                            <PdfTableHeader
                              partyRequired={"required"}
                              start_date={startDate}
                              end_date={endDate}
                              platform={platform}
                              data={val.top_pages}
                            />
                            <PdfTableBody
                              data={val.top_pages}
                              start_date={startDate}
                              end_date={endDate}
                              partyRequired={"required"}
                              platform={platform}
                            />
                          </View>
                        ) : (
                          <View style={styles.table}>
                            <PdfTableHeaderWeekly
                              partyRequired={"required"}
                              start_date={startDate}
                              end_date={endDate}
                              platform={platform}
                              reportType={dateDiffInDays(startDate, endDate)}
                              data={val.top_pages}
                            />
                            <PdfTableBodyWeekly
                              data={val.top_pages}
                              start_date={startDate}
                              end_date={endDate}
                              partyRequired={"required"}
                              platform={platform}
                            />
                          </View>
                        )
                      ) : (
                        <></>
                      )}

                      {val?.top_posts && startDate === endDate ? (
                        <View break>
                          <View style={styles.reportHeadingNew}>
                            <Text style={styles.headingTextNew}>
                              {String(platform).toLowerCase() === "youtube"
                                ? "Top Videos"
                                : "Top Posts"}
                            </Text>
                          </View>
                          <View style={styles.imageContainer}>
                            {val.top_posts.map((imgVal) => {
                              if (imgVal?.resource?.startsWith("https://")) {
                                return (
                                  <ImageCardPdf
                                    key={imgVal.post_id}
                                    image={imgVal.resource}
                                    shares={imgVal.shares}
                                    name={imgVal.page_name}
                                    type="image"
                                    platform={platform}
                                    postLink={imgVal.post_link}
                                  />
                                );
                              } else if (
                                imgVal?.resource &&
                                imgVal?.resource !== ""
                              ) {
                                return (
                                  <ImageCardPdf
                                    key={imgVal.post_id}
                                    image={imgVal.resource}
                                    shares={imgVal.shares}
                                    name={imgVal.page_name}
                                    type="text"
                                    platform={platform}
                                    postLink={imgVal.post_link}
                                  />
                                );
                              }
                            })}
                          </View>
                        </View>
                      ) : (
                        <></>
                      )}

                      {val[`Bharatiya Janata Party`] || val[`BJP`] ? (
                        startDate === endDate ? (
                          <View style={styles.table}>
                            <PdfTableHeaderNoParty
                              partyRequired={""}
                              start_date={startDate}
                              end_date={endDate}
                              heading={"Bharatiya Janata Party"}
                              platform={platform}
                              data={val[`Bharatiya Janata Party`] || val[`BJP`]}
                            />
                            <PdfTableBodyNoParty
                              data={val[`Bharatiya Janata Party`] || val[`BJP`]}
                              start_date={startDate}
                              end_date={endDate}
                              partyRequired={""}
                              platform={platform}
                            />
                          </View>
                        ) : (
                          <View style={styles.table}>
                            <PdfTableHeaderWeeklyNoParty
                              partyRequired={""}
                              start_date={startDate}
                              end_date={endDate}
                              heading={"Bharatiya Janata Party"}
                              platform={platform}
                              reportType={dateDiffInDays(startDate, endDate)}
                              data={val[`Bharatiya Janata Party`] || val[`BJP`]}
                            />
                            <PdfTableBodyWeeklyNoParty
                              data={val[`Bharatiya Janata Party`] || val[`BJP`]}
                              start_date={startDate}
                              end_date={endDate}
                              partyRequired={""}
                              platform={platform}
                            />
                          </View>
                        )
                      ) : (
                        <></>
                      )}

                      {val[`Indian National Congress`] || val[`INC`] ? (
                        startDate === endDate ? (
                          <View style={styles.table}>
                            <PdfTableHeaderNoParty
                              partyRequired={""}
                              start_date={startDate}
                              end_date={endDate}
                              heading={"Indian National Congress"}
                              platform={platform}
                              data={
                                val[`Indian National Congress`] || val[`INC`]
                              }
                            />
                            <PdfTableBodyNoParty
                              data={
                                val[`Indian National Congress`] || val[`INC`]
                              }
                              start_date={startDate}
                              end_date={endDate}
                              partyRequired={""}
                              platform={platform}
                            />
                          </View>
                        ) : (
                          <View style={styles.table}>
                            <PdfTableHeaderWeeklyNoParty
                              partyRequired={""}
                              start_date={startDate}
                              end_date={endDate}
                              heading={"Indian National Congress"}
                              platform={platform}
                              reportType={dateDiffInDays(startDate, endDate)}
                              data={
                                val[`Indian National Congress`] || val[`INC`]
                              }
                            />
                            <PdfTableBodyWeeklyNoParty
                              data={
                                val[`Indian National Congress`] || val[`INC`]
                              }
                              start_date={startDate}
                              end_date={endDate}
                              partyRequired={""}
                              platform={platform}
                            />
                          </View>
                        )
                      ) : (
                        <></>
                      )}

                      {val[`Aam Aadmi Party`] || val[`AAP`] ? (
                        startDate === endDate ? (
                          <View style={styles.table}>
                            <PdfTableHeaderNoParty
                              partyRequired={""}
                              start_date={startDate}
                              end_date={endDate}
                              heading={"Aam Aadmi Party"}
                              platform={platform}
                              data={val[`Aam Aadmi Party`] || val[`AAP`]}
                            />
                            <PdfTableBodyNoParty
                              data={val[`Aam Aadmi Party`] || val[`AAP`]}
                              start_date={startDate}
                              end_date={endDate}
                              partyRequired={""}
                              platform={platform}
                            />
                          </View>
                        ) : (
                          <View style={styles.table}>
                            <PdfTableHeaderWeeklyNoParty
                              partyRequired={""}
                              start_date={startDate}
                              end_date={endDate}
                              heading={"Aam Aadmi Party"}
                              platform={platform}
                              reportType={dateDiffInDays(startDate, endDate)}
                              data={val[`Aam Aadmi Party`] || val[`AAP`]}
                            />
                            <PdfTableBodyWeeklyNoParty
                              data={val[`Aam Aadmi Party`] || val[`AAP`]}
                              start_date={startDate}
                              end_date={endDate}
                              partyRequired={""}
                              platform={platform}
                            />
                          </View>
                        )
                      ) : (
                        <></>
                      )}

                      {val ? (
                        Object.keys(val).map((key) => {
                          return (
                            <Fragment>
                              {key !== "top_posts" &&
                              key !== "top_pages" &&
                              key !== "Bharatiya Janata Party" &&
                              key !== "BJP" &&
                              key !== "Indian National Congress" &&
                              key !== "INC" &&
                              key !== "AAP" &&
                              key !== "Aam Aadmi Party" ? (
                                startDate === endDate ? (
                                  <View style={styles.table}>
                                    <PdfTableHeaderNoParty
                                      partyRequired={""}
                                      start_date={startDate}
                                      end_date={endDate}
                                      heading={key}
                                      platform={platform}
                                      data={val[`${key}`]}
                                    />
                                    <PdfTableBodyNoParty
                                      data={val[`${key}`]}
                                      start_date={startDate}
                                      end_date={endDate}
                                      partyRequired={""}
                                      platform={platform}
                                    />
                                  </View>
                                ) : (
                                  <View style={styles.table}>
                                    <PdfTableHeaderWeeklyNoParty
                                      partyRequired={""}
                                      start_date={startDate}
                                      end_date={endDate}
                                      heading={key}
                                      platform={platform}
                                      reportType={dateDiffInDays(
                                        startDate,
                                        endDate
                                      )}
                                      data={val[`${key}`]}
                                    />
                                    <PdfTableBodyWeeklyNoParty
                                      data={val[`${key}`]}
                                      start_date={startDate}
                                      end_date={endDate}
                                      partyRequired={""}
                                      platform={platform}
                                    />
                                  </View>
                                )
                              ) : (
                                <></>
                              )}
                            </Fragment>
                          );
                        })
                      ) : (
                        <></>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <></>
              )}
            </View>
            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) =>
                `${pageNumber} / ${totalPages}`
              }
              fixed
            />
          </Page>
        </Document>
      ) : (
        <Document>
          <Page
            size="A4"
            style={startDate === endDate ? styles.page : styles.pageWeekly}
            orientation="portrait"
            wrap
          >
            <View style={styles.body}>
              <Text style={styles.confidentialText}>Confidential</Text>
              <View style={styles.reportHeadingSmar}>
                <Text style={styles.headingSmarTextTailWind}>
                  Social Media Analytics Report
                </Text>
                <Text style={styles.subHeadingText}>{fileName}</Text>
                {platform === "instagram" ? (
                  <Text style={styles.headingTextTailWind}>
                    {" "}
                    {dateDiffInDays(startDate, endDate) === 0
                      ? "Instagram - Daily Report"
                      : dateDiffInDays(startDate, endDate) === 6
                      ? "Instagram - Weekly Report"
                      : "Instagram - Monthly Report"}{" "}
                    <Text style={styles.headingTextDateTailWind}>
                      {startDate !== endDate
                        ? `(${getDayName(
                            startDate,
                            "range",
                            true
                          )} - ${getDayName(endDate, "range", true)})`
                        : `(${formatDate(startDate)})`}
                    </Text>
                  </Text>
                ) : platform === "youtube" ? (
                  <Text style={styles.headingTextTailWind}>
                    {dateDiffInDays(startDate, endDate) === 0
                      ? "Youtube - Daily Report "
                      : dateDiffInDays(startDate, endDate) === 6
                      ? "Youtube - Weekly Report"
                      : "Youtube - Monthly Report"}{" "}
                    <Text style={styles.headingTextDateTailWind}>
                      {startDate !== endDate
                        ? `(${getDayName(
                            startDate,
                            "range",
                            true
                          )} - ${getDayName(endDate, "range", true)})`
                        : `(${formatDate(startDate)})`}
                    </Text>
                  </Text>
                ) : (
                  <Text style={styles.headingTextTailWind}>
                    {dateDiffInDays(startDate, endDate) === 0
                      ? `${platform === "twitter" ? "Twitter" : "Facebook"} - Daily Report `
                      : dateDiffInDays(startDate, endDate) === 6
                      ? `${platform === "twitter" ? "Twitter" : "Facebook"} - Weekly Report`
                      : `${platform === "twitter" ? "Twitter" : "Facebook"} - Monthly Report`}{" "}
                    <Text style={styles.headingTextDateTailWind}>
                      {startDate !== endDate
                        ? `(${getDayName(
                            startDate,
                            "range",
                            true
                          )} - ${getDayName(endDate, "range", true)})`
                        : `(${formatDate(startDate)})`}
                    </Text>
                  </Text>
                )}
              </View>
              {reportData.length > 0 ? (
                reportData.map((val, indx) => {
                  return (
                    <Fragment key={indx}>
                      {val?.top_pages &&
                      fileName !== "National Parties - Instagram" &&
                      fileName !== "National Parties - Facebook" ? (
                        startDate === endDate ? (
                          <View style={styles.table}>
                            <PdfTableHeaderTailWind
                              partyRequired={"required"}
                              start_date={startDate}
                              end_date={endDate}
                              platform={platform}
                              data={val.top_pages}
                              reportType={reportType}
                              fileName={fileName}
                              reportId={reportId}
                            />
                            <PdfTableBodyTailWind
                              data={val.top_pages}
                              start_date={startDate}
                              end_date={endDate}
                              partyRequired={"required"}
                              platform={platform}
                              fileName={fileName}
                              reportId={reportId}
                            />
                          </View>
                        ) : (
                          <View style={styles.table}>
                            <PdfTableHeaderTailWindWeekly
                              partyRequired={"required"}
                              start_date={startDate}
                              end_date={endDate}
                              platform={platform}
                              data={val.top_pages}
                              reportType={reportType}
                              fileName={fileName}
                              reportId={reportId}
                            />
                            <PdfTableBodyTailWindWeekly
                              data={val.top_pages}
                              start_date={startDate}
                              end_date={endDate}
                              partyRequired={"required"}
                              platform={platform}
                              fileName={fileName}
                              reportId={reportId}
                            />
                          </View>
                        )
                      ) : startDate === endDate ? (
                        <View style={styles.table}>
                          <PdfTableHeaderNoPartyTailWind
                            partyRequired={""}
                            start_date={startDate}
                            end_date={endDate}
                            platform={platform}
                            data={val.top_pages}
                            reportType={reportType}
                            fileName={fileName}
                            reportId={reportId}
                          />
                        </View>
                      ) : (
                        <View style={styles.table}>
                          <PdfTableHeaderNoPartyTailWindWeekly
                            partyRequired={""}
                            start_date={startDate}
                            end_date={endDate}
                            platform={platform}
                            data={val.top_pages}
                            reportType={reportType}
                            fileName={fileName}
                            reportId={reportId}
                          />
                        </View>
                      )}
                      <View style={styles.chartContainer} break>
                        {chartImage && (
                          <View>
                            <Text style={styles.headingTextNewTailwind}>
                              {startDate === endDate
                                ? `COMPARATIVE PERFORMANCE ACROSS LAST FIVE DAYS `
                                : `COMPARATIVE PERFORMANCE ACROSS LAST FIVE WEEKS `}
                            </Text>
                            <Image style={styles.chart} src={chartImage} />
                            <Text style={styles.subHeadingTextNewTailwind}>
                              {`Note: Bars indicate total number of ${
                                platform === "youtube"
                                  ? "videos"
                                  : platform === "twitter"
                                  ? "tweets"
                                  : "posts"
                              } in a ${
                                startDate === endDate ? "day" : "week"
                              } and lines indicate total ${
                                platform === "instagram"
                                  ? "likes"
                                  : platform === "youtube"
                                  ? "Views"
                                  : platform === "twitter"
                                  ? "Retweets"
                                  : "Shares"
                              } in a ${startDate === endDate ? "day" : "week"}`}
                            </Text>
                          </View>
                        )}
                        {tableData && tableData.length > 0 && (
                          <View>
                            <PdfDateRangeTable
                              data={tableData}
                              fileName={fileName}
                              platform={platform}
                            />
                            <Text style={styles.subHeadingTextNewTailwindRight}>
                              {`Data in table represents: Cumulative ${
                                platform === "youtube"
                                  ? "Videos"
                                  : platform === "twitter"
                                  ? "Tweets"
                                  : "Posts"
                              } | Cumulative ${
                                platform === "instagram"
                                  ? "Likes"
                                  : platform === "youtube"
                                  ? "Views"
                                  : platform === "twitter"
                                  ? "Retweets"
                                  : "Shares"
                              }`}
                            </Text>
                          </View>
                        )}
                      </View>

                      {val?.top_posts && val.top_posts.length > 0 && (
                        <View break style={{ minHeight: "500" }}>
                          <View style={styles.reportHeadingNew}>
                            <Text style={styles.headingTextNewTailwind}>
                              {platform === "twitter" ? "Top Tweets" : "Top Posts"}
                            </Text>
                          </View>
                          <View style={styles.imageContainer}>
                            {val.top_posts.map((imgVal) => (
                              <Card
                                key={imgVal.post_id}
                                title={imgVal.page_name}
                                description={imgVal.message}
                                shares={imgVal.shares}
                                likes={imgVal.likes}
                                comments={imgVal.comments}
                                imageUrl={
                                  /^https?:\/\//.test(imgVal.resource || "")
                                    ? imgVal.resource
                                    : ""
                                }
                                postLink={imgVal.post_link}
                                platform={platform}
                                reportType={reportType}
                              />
                            ))}
                          </View>
                        </View>
                      )}

                      {/* {val && instaCheck.includes(reportId)
                        ? Object.keys(val).map((key) => {
                            if (
                              fileName === "Ireland TDs" ||
                              fileName === "Ireland National Ministers"
                            ) {
                              if (startDate === endDate) {
                                return (
                                  <Fragment key={key}>
                                    {key !== "top_pages" &&
                                    key !== "top_posts" &&
                                    fileName !== "Ireland National Leaders" &&
                                    fileName !== "Ireland National Parties" ? (
                                      <View
                                        break={
                                          fileName === "Ireland TDs"
                                            ? key === "Fianna Fáil" ||
                                              key ===
                                                "Right to Change (Independent Group)" ||
                                              key === "Labour" ||
                                              key === "Sinn Féin"
                                            : key === "Fianna Fáil" ||
                                              key === "Fine Gael" ||
                                              key === "Green Party Ireland"
                                        }
                                        style={styles.table_tailwind}
                                      >
                                        <PdfTableHeaderNoPartyTailWind
                                          partyRequired={""}
                                          start_date={startDate}
                                          end_date={endDate}
                                          heading={key}
                                          platform={platform}
                                          data={val[`${key}`]}
                                          reportType={reportType}
                                          reportId={reportId}
                                        />
                                      </View>
                                    ) : (
                                      <></>
                                    )}
                                  </Fragment>
                                );
                              } else {
                                return (
                                  <Fragment key={key}>
                                    {key !== "top_pages" &&
                                    key !== "top_posts" &&
                                    fileName !== "Ireland National Leaders" &&
                                    fileName !== "Ireland National Parties" ? (
                                      <View
                                        break={
                                          key === "Fianna Fáil" ||
                                          key ===
                                            "Right to Change (Independent Group)" ||
                                          key === "Independent" ||
                                          key === "Sinn Féin"
                                        }
                                        style={styles.table_tailwind}
                                      >
                                        <PdfTableHeaderNoPartyTailWindWeekly
                                          partyRequired={""}
                                          start_date={startDate}
                                          end_date={endDate}
                                          heading={key}
                                          platform={platform}
                                          data={val[`${key}`]}
                                          reportType={reportType}
                                          reportId={reportId}
                                        />
                                      </View>
                                    ) : (
                                      <></>
                                    )}
                                  </Fragment>
                                );
                              }
                            } else {
                              if (startDate === endDate) {
                                return (
                                  <Fragment key={key}>
                                    {key !== "top_pages" &&
                                    key !== "top_posts" &&
                                    fileName !== "Ireland National Leaders" &&
                                    fileName !== "Ireland National Parties" ? (
                                      <View style={styles.table}>
                                        <PdfTableNoPartyTailWind
                                          partyRequired={""}
                                          start_date={startDate}
                                          end_date={endDate}
                                          heading={key}
                                          platform={platform}
                                          data={val[`${key}`]}
                                          reportType={reportType}
                                          reportId={reportId}
                                        />
                                      </View>
                                    ) : (
                                      <></>
                                    )}
                                  </Fragment>
                                );
                              } else {
                                return (
                                  <Fragment key={key}>
                                    {key !== "top_pages" &&
                                    key !== "top_posts" &&
                                    fileName !== "Ireland National Leaders" &&
                                    fileName !== "Ireland National Parties" ? (
                                      <View style={styles.table}>
                                        <PdfTableNoPartyTailWindWeekly
                                          partyRequired={""}
                                          start_date={startDate}
                                          end_date={endDate}
                                          heading={key}
                                          platform={platform}
                                          data={val[`${key}`]}
                                          reportType={reportType}
                                          reportId={reportId}
                                        />
                                      </View>
                                    ) : (
                                      <></>
                                    )}
                                  </Fragment>
                                );
                              }
                            }
                          })
                        : Object.keys(val).map((key,index) => {
                            if (
                              fileName === "Ireland TDs" ||
                              fileName === "Ireland National Ministers"
                            ) {
                              if (startDate === endDate) {
                                return (
                                  <Fragment key={key}>
                                    {key !== "top_pages" &&
                                    key !== "top_posts" &&
                                    fileName !== "Ireland National Leaders" &&
                                    fileName !== "Ireland National Parties" ? (
                                      <View
                                        break={index===2 || strictPageBreak?.[
                                          `${fileName}`
                                        ].includes(key)}
                                        style={styles.table_tailwind}
                                      >
                                        <PdfTableHeaderNoPartyTailWind
                                          partyRequired={""}
                                          start_date={startDate}
                                          end_date={endDate}
                                          heading={key}
                                          platform={platform}
                                          data={val[`${key}`]}
                                          reportType={reportType}
                                          reportId={reportId}
                                        />
                                      </View>
                                    ) : (
                                      <></>
                                    )}
                                  </Fragment>
                                );
                              } else {
                                return (
                                  <Fragment key={key}>
                                    {key !== "top_pages" &&
                                    key !== "top_posts" &&
                                    fileName !== "Ireland National Leaders" &&
                                    fileName !== "Ireland National Parties" ? (
                                      <View
                                        break={index===2 ||strictPageBreak?.[
                                          `${fileName}`
                                        ].includes(key)}
                                        style={styles.table_tailwind}
                                      >
                                        <PdfTableHeaderNoPartyTailWindWeekly
                                          partyRequired={""}
                                          start_date={startDate}
                                          end_date={endDate}
                                          heading={key}
                                          platform={platform}
                                          data={val[`${key}`]}
                                          reportType={reportType}
                                          reportId={reportId}
                                        />
                                      </View>
                                    ) : (
                                      <></>
                                    )}
                                  </Fragment>
                                );
                              }
                            } else {
                              if (startDate === endDate) {
                                return (
                                  <Fragment key={key}>
                                    {key !== "top_pages" &&
                                    key !== "top_posts" &&
                                    fileName !== "Ireland National Leaders" &&
                                    fileName !== "Ireland National Parties" ? (
                                      <View style={styles.table}>
                                        <PdfTableNoPartyTailWind
                                          partyRequired={""}
                                          start_date={startDate}
                                          end_date={endDate}
                                          heading={key}
                                          platform={platform}
                                          data={val[`${key}`]}
                                          reportType={reportType}
                                          reportId={reportId}
                                        />
                                      </View>
                                    ) : (
                                      <></>
                                    )}
                                  </Fragment>
                                );
                              } else {
                                return (
                                  <Fragment key={key}>
                                    {key !== "top_pages" &&
                                    key !== "top_posts" &&
                                    fileName !== "Ireland National Leaders" &&
                                    fileName !== "Ireland National Parties" ? (
                                      <View style={styles.table} break={index===2}>
                                        <PdfTableNoPartyTailWindWeekly
                                          partyRequired={""}
                                          start_date={startDate}
                                          end_date={endDate}
                                          heading={key}
                                          platform={platform}
                                          data={val[`${key}`]}
                                          reportType={reportType}
                                          reportId={reportId}
                                        />
                                      </View>
                                    ) : (
                                      <></>
                                    )}
                                  </Fragment>
                                );
                              }
                            }
                          })} */}
                    </Fragment>
                  );
                })
              ) : (
                <></>
              )}
            </View>
            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) =>
                `${pageNumber} / ${totalPages}`
              }
              fixed
            />
          </Page>
        </Document>
      )}
    </>
  );
};

export default GeneratePdf;
